import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  useMediaQuery,
  Alert,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Skeleton,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  GridToolbarColumnsButton,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { useTheme } from "@mui/material/styles";

const EMPTY_ROWS = [];

/* ---------- Toolbar moderno ---------- */
function DefaultToolbar({ hideExport = false } = {}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <GridToolbarContainer
      sx={{
        px: 1,
        py: 0.75,
        gap: 1,
        borderBottom: isDark
          ? "1px solid rgba(255,255,255,0.06)"
          : "1px solid rgba(0,0,0,0.06)",
        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.72)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        "& .MuiButton-root": {
          minWidth: "auto",
          padding: "6px",
          width: 36,
          height: 36,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: isDark ? theme.palette.grey[300] : theme.palette.grey[700],
          borderRadius: 2,
          background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.03)",
          border: "none",
        },
        "& .MuiButton-startIcon": {
          margin: 0,
        },
        "& .MuiButton-root:hover": {
          background: isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.06)",
        },
        "& .MuiSvgIcon-root": { fontSize: "20px" },
      }}
    >
      <GridToolbarColumnsButton />
      {!hideExport && <GridToolbarExport />}
      <GridToolbarQuickFilter
        debounceMs={450}
        placeholder="Buscar..."
        sx={{
          marginLeft: "auto",
          "& .MuiInputBase-root": {
            fontSize: 14,
            borderRadius: 999,
            px: 1,
            background: "rgba(0,0,0,0.04)",
          },
        }}
      />
    </GridToolbarContainer>
  );
}

/* ---------- Helpers: estilizar IconButtons sin tocar columns ---------- */
function styleIconButtonsDeep(node, sxFn) {
  if (!React.isValidElement(node)) return node;

  if (node.type === IconButton) {
    const prevSx = node.props?.sx;
    const mergedSx = Array.isArray(prevSx)
      ? [...prevSx, sxFn(node)]
      : [prevSx, sxFn(node)].filter(Boolean);

    return React.cloneElement(node, {
      size: node.props.size || "small",
      sx: mergedSx,
    });
  }

  const children = node.props?.children;
  if (!children) return node;

  const nextChildren = React.Children.map(children, (ch) =>
    styleIconButtonsDeep(ch, sxFn),
  );

  return React.cloneElement(node, {}, nextChildren);
}

const modernActionButtonSx = () => (el) => {
  const color = el.props?.color || "default";

  const base = {
    borderRadius: 2,
    width: 34,
    height: 34,
    bgcolor: "rgba(0,0,0,0.04)",
    border: "1px solid rgba(0,0,0,0.06)",
    transition: "all .16s ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 10px 24px rgba(0,0,0,0.10)",
      bgcolor: "rgba(0,0,0,0.06)",
    },
    "& .MuiSvgIcon-root": { fontSize: 20 },
  };

  if (color === "primary") {
    return {
      ...base,
      border: "1px solid rgba(25,118,210,0.20)",
      bgcolor: "rgba(25,118,210,0.08)",
      "&:hover": { ...base["&:hover"], bgcolor: "rgba(25,118,210,0.12)" },
    };
  }
  if (color === "error") {
    return {
      ...base,
      border: "1px solid rgba(211,47,47,0.18)",
      bgcolor: "rgba(211,47,47,0.06)",
      "&:hover": { ...base["&:hover"], bgcolor: "rgba(211,47,47,0.10)" },
    };
  }
  if (color === "success") {
    return {
      ...base,
      border: "1px solid rgba(46,125,50,0.18)",
      bgcolor: "rgba(46,125,50,0.06)",
      "&:hover": { ...base["&:hover"], bgcolor: "rgba(46,125,50,0.10)" },
    };
  }

  return base;
};

function MobileCard({ children, sx }) {
  return (
    <Paper
      elevation={0}
      sx={(theme) => {
        const isDark = theme.palette.mode === "dark";
        return {
          p: 1.6,
          mb: 1.3,
          borderRadius: 4,
          border: isDark
            ? "1px solid rgba(255,255,255,0.08)"
            : "1px solid rgba(0,0,0,0.08)",
          background: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.86)",
          boxShadow: isDark
            ? "0 12px 34px rgba(0,0,0,0.3)"
            : "0 12px 34px rgba(0,0,0,0.06)",
          ...sx,
        };
      }}
    >
      {children}
    </Paper>
  );
}

export default function DataGridCustom({
  rows = EMPTY_ROWS,
  columns = [],
  title = "",
  description = "",
  icon = null,
  headerActions = null,
  toolbar = DefaultToolbar,
  hideExport = false,
  error = "",

  // pagination
  paginationModel, // { page, pageSize }
  onPaginationChange, // (model)=>void
  total, // rowCount real cuando es server
  loading = false,
  filterModel,
  onFilterChange,
  filterMode = "client",
  isServerPagination = false,

  getRowId = (row) => row.id,
  pageSizeOptions = [10, 20, 50, 100],
  getRowHeight,
  datagridSx = {},

  // mobile local
  mobilePageSize = 5,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";

  const safeRows = Array.isArray(rows) ? rows : EMPTY_ROWS;

  // total real: server => total, local => rows.length
  const effectiveTotal = useMemo(() => {
    if (isServerPagination) return typeof total === "number" ? total : 0;
    return safeRows.length;
  }, [isServerPagination, total, safeRows.length]);

  // local pagination SOLO para modo local
  const [localPage, setLocalPage] = useState(0);

  // page/pageSize fuente de verdad
  const page = isServerPagination ? (paginationModel?.page ?? 0) : localPage;
  const pageSize = isServerPagination
    ? (paginationModel?.pageSize ?? 10)
    : mobilePageSize;

  const pageCount = useMemo(() => {
    const t = effectiveTotal || 0;
    return Math.max(1, Math.ceil(t / pageSize));
  }, [effectiveTotal, pageSize]);

  // ✅ FIX LOCAL: reset / clamp sin tocar server (NO loops)
  useEffect(() => {
    if (isServerPagination) return;

    // si cambian filas o filtros, volvemos al inicio
    setLocalPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isServerPagination, safeRows.length, filterModel]);

  useEffect(() => {
    if (isServerPagination) return;

    // clamp: por si el dataset se achica y quedaste fuera
    setLocalPage((p) => Math.min(p, pageCount - 1));
  }, [isServerPagination, pageCount]);

  const canPrev = page > 0;
  const canNext = page + 1 < pageCount;

  const [mobileSearch, setMobileSearch] = useState("");

  const handleMobileSearchChange = useCallback(
    (value) => {
      setMobileSearch(value);
      onFilterChange?.({
        items: filterModel?.items || [],
        quickFilterValues: value ? [value] : [],
      });
    },
    [filterModel, onFilterChange],
  );

  // filas a renderizar:
  const paginatedRows = useMemo(() => {
    if (isServerPagination) return safeRows; // server ya viene paginado
    return safeRows.slice(page * pageSize, (page + 1) * pageSize);
  }, [safeRows, isServerPagination, page, pageSize]);

  const styledColumns = useMemo(() => {
    if (!Array.isArray(columns)) return [];

    return columns.map((col) => {
      if (typeof col.renderCell !== "function") return col;

      return {
        ...col,

        // ✅ centramos columna completa
        ...(col.field === "actions" && {
          align: "center",
          headerAlign: "center",
        }),

        renderCell: (params) => {
          const rendered = col.renderCell(params);
          const styled = styleIconButtonsDeep(
            rendered,
            modernActionButtonSx(theme),
          );

          if (col.field === "actions") {
            return (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {styled}
              </Box>
            );
          }

          return styled;
        },
      };
    });
  }, [columns, theme]);

  // handlers (sin effects -> sin loop)
  const goPrev = () => {
    if (!canPrev) return;
    const newPage = page - 1;
    if (isServerPagination) onPaginationChange?.({ page: newPage, pageSize });
    else setLocalPage(newPage);
  };

  const goNext = () => {
    if (!canNext) return;
    const newPage = page + 1;
    if (isServerPagination) onPaginationChange?.({ page: newPage, pageSize });
    else setLocalPage(newPage);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.2, sm: 2.4 },
        borderRadius: { xs: 3, sm: 4 },
        border: isDark
          ? "1px solid rgba(255,255,255,0.08)"
          : "1px solid rgba(0,0,0,0.08)",
        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.78)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        boxShadow: isDark
          ? "0 14px 40px rgba(0,0,0,0.28)"
          : "0 14px 40px rgba(0,0,0,0.06)",
      }}
    >
      {/* Header */}
      {(title || description || icon) && (
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1.5,
            mb: { xs: 1.4, sm: 2 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1.2, sm: 1.4 },
              minWidth: 0,
            }}
          >
            {icon && (
              <Box
                sx={{
                  width: { xs: 42, sm: 56 },
                  height: { xs: 42, sm: 56 },
                  borderRadius: 3,
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                  background: isDark
                    ? "rgba(25,118,210,0.10)"
                    : "rgba(255,255,255,0.72)",
                  border: "1px solid rgba(25,118,210,0.22)",
                  boxShadow: "0 8px 22px rgba(0,0,0,0.08)",
                  color: "primary.main",
                  "& svg": { fontSize: { xs: 22, sm: 28 }, display: "block" },
                }}
              >
                {icon}
              </Box>
            )}

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: { xs: 15, sm: 18 },
                  fontWeight: 950,
                  lineHeight: 1.15,
                  mb: description ? 0.35 : 0,
                }}
              >
                {title}
              </Typography>

              {description ? (
                <Typography
                  sx={{
                    fontSize: 13.5,
                    opacity: 0.72,
                    lineHeight: 1.35,
                    maxWidth: 560,
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  {description}
                </Typography>
              ) : null}
            </Box>
          </Box>

          {headerActions && (
            <Box sx={{ flexShrink: 0 }}>{headerActions}</Box>
          )}
        </Box>
      )}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {/* MOBILE: Cards */}
      {isMobile ? (
        <>
          <TextField
            size="small"
            placeholder="Buscar..."
            value={mobileSearch}
            onChange={(e) => handleMobileSearchChange(e.target.value)}
            fullWidth
            sx={{
              mb: 1.5,
              "& .MuiInputBase-root": {
                borderRadius: 3,
                fontSize: 14,
                bgcolor: isDark
                  ? "rgba(255,255,255,0.06)"
                  : "rgba(0,0,0,0.04)",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ fontSize: 18, opacity: 0.5 }} />
                </InputAdornment>
              ),
              endAdornment: mobileSearch ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => handleMobileSearchChange("")}
                    edge="end"
                    sx={{ mr: -0.5 }}
                  >
                    <CloseRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {loading ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {[...Array(mobilePageSize)].map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={72}
                  sx={{ borderRadius: 3, opacity: 0.5 - i * 0.06 }}
                />
              ))}
            </Box>
          ) : null}

          {!loading && paginatedRows.length === 0 ? (
            <Box
              sx={{
                py: 5,
                px: 2,
                borderRadius: 3,
                border: isDark
                  ? "1px dashed rgba(255,255,255,0.14)"
                  : "1px dashed rgba(0,0,0,0.15)",
                background: isDark
                  ? "rgba(255,255,255,0.03)"
                  : "rgba(0,0,0,0.01)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
              }}
            >
              <InboxRoundedIcon sx={{ fontSize: 38, opacity: 0.22 }} />
              <Typography
                sx={{ fontWeight: 700, fontSize: 13.5, opacity: 0.45 }}
              >
                Sin resultados
              </Typography>
            </Box>
          ) : null}

          {!loading &&
            paginatedRows.map((row) => {
              const rowId = getRowId(row);

              const actionsCol = columns.find((c) => c.field === "actions");
              const actionsNode = actionsCol?.renderCell
                ? styleIconButtonsDeep(
                    actionsCol.renderCell({
                      row,
                      value: row["actions"],
                      field: "actions",
                      id: rowId,
                    }),
                    modernActionButtonSx(theme),
                  )
                : null;

              return (
                <MobileCard key={rowId}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    {columns.map((col) => {
                      if (col.field === "actions") return null;

                      const label = col.headerName || col.field;
                      const raw = row[col.field];

                      // Apply valueGetter like DataGrid does on desktop
                      let displayValue = raw;
                      if (typeof col.valueGetter === "function") {
                        displayValue = col.valueGetter(raw, row);
                      }

                      const valueNode = col.renderCell
                        ? styleIconButtonsDeep(
                            col.renderCell({
                              row,
                              value: displayValue,
                              field: col.field,
                              id: rowId,
                            }),
                            modernActionButtonSx(theme),
                          )
                        : (() => {
                            // Apply valueFormatter like DataGrid does on desktop
                            if (typeof col.valueFormatter === "function") {
                              return col.valueFormatter(displayValue, row);
                            }
                            if (
                              typeof displayValue === "object" &&
                              displayValue !== null
                            ) {
                              return `${displayValue?.name ?? ""} ${displayValue?.lastname ?? ""}`.trim();
                            }
                            // Auto-format ISO date strings (d/m/YYYY HH:mm)
                            if (
                              typeof displayValue === "string" &&
                              /^\d{4}-\d{2}-\d{2}T/.test(displayValue)
                            ) {
                              const d = new Date(displayValue);
                              if (!isNaN(d.getTime())) {
                                const p = (n) => String(n).padStart(2, "0");
                                return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
                              }
                            }
                            return displayValue;
                          })();

                      const isReactEl = React.isValidElement(valueNode);

                      return (
                        <Box
                          key={col.field}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1.5,
                            py: 0.8,
                            borderBottom: isDark
                              ? "1px solid rgba(255,255,255,0.055)"
                              : "1px solid rgba(0,0,0,0.055)",
                            "&:last-child": { borderBottom: "none" },
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              opacity: 0.52,
                              fontWeight: 700,
                              flexShrink: 0,
                              maxWidth: "44%",
                              lineHeight: 1.3,
                            }}
                          >
                            {label}
                          </Typography>

                          {isReactEl ? (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                minWidth: 0,
                              }}
                            >
                              {valueNode}
                            </Box>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 800,
                                lineHeight: 1.25,
                                wordBreak: "break-word",
                                textAlign: "right",
                                minWidth: 0,
                                flex: 1,
                              }}
                            >
                              {valueNode ?? "—"}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>

                  {actionsNode ? (
                    <Box
                      sx={{
                        mt: 1.4,
                        pt: 1.2,
                        borderTop: isDark
                          ? "1px solid rgba(255,255,255,0.08)"
                          : "1px solid rgba(0,0,0,0.08)",
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      {actionsNode}
                    </Box>
                  ) : null}
                </MobileCard>
              );
            })}

          {/* Pagination mobile */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 1.8,
              gap: 1,
            }}
          >
            <IconButton
              disabled={!canPrev || loading}
              onClick={goPrev}
              size="small"
              sx={{
                borderRadius: 2,
                width: 36,
                height: 36,
                border: isDark
                  ? "1px solid rgba(255,255,255,0.12)"
                  : "1px solid rgba(0,0,0,0.12)",
                bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                "&:hover": {
                  bgcolor: isDark
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.06)",
                },
              }}
            >
              <ChevronLeftRoundedIcon sx={{ fontSize: 20 }} />
            </IconButton>

            <Chip
              label={`${page + 1} / ${pageCount}`}
              size="small"
              sx={{
                borderRadius: 2,
                fontWeight: 700,
                fontSize: 13,
                px: 0.5,
                bgcolor: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)",
                border: isDark
                  ? "1px solid rgba(255,255,255,0.10)"
                  : "1px solid rgba(0,0,0,0.09)",
              }}
            />

            <IconButton
              disabled={!canNext || loading}
              onClick={goNext}
              size="small"
              sx={{
                borderRadius: 2,
                width: 36,
                height: 36,
                border: isDark
                  ? "1px solid rgba(255,255,255,0.12)"
                  : "1px solid rgba(0,0,0,0.12)",
                bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                "&:hover": {
                  bgcolor: isDark
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.06)",
                },
              }}
            >
              <ChevronRightRoundedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </>
      ) : (
        /* DESKTOP */
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <DataGrid
            rows={safeRows}
            columns={styledColumns}
            getRowId={getRowId}
            autoHeight
            loading={loading}
            disableRowSelectionOnClick
            disableColumnMenu
            slots={{ toolbar }}
            slotProps={{ toolbar: { hideExport } }}
            showToolbar
            sx={{
              border: "none",
              "--DataGrid-containerBackground": "transparent",
              "--DataGrid-pinnedBackground": "transparent",
              "& .MuiDataGrid-columnHeaders": {
                background: isDark
                  ? "rgba(255,255,255,0.04)"
                  : "rgba(0,0,0,0.025)",
                borderBottom: isDark
                  ? "1px solid rgba(255,255,255,0.07)"
                  : "1px solid rgba(0,0,0,0.07)",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
                fontSize: 12.5,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.50)",
              },
              "& .MuiDataGrid-columnSeparator--resizable": { opacity: 0, transition: "opacity .15s ease", "&:hover": { opacity: 1 } },
              "& .MuiDataGrid-row:hover": {
                background: isDark
                  ? "rgba(255,255,255,0.04)"
                  : "rgba(25,118,210,0.04)",
              },
              "& .MuiDataGrid-cell": {
                fontSize: 14,
                borderColor: isDark
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.05)",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: isDark
                  ? "1px solid rgba(255,255,255,0.07)"
                  : "1px solid rgba(0,0,0,0.07)",
                minHeight: 52,
              },
              "& .MuiDataGrid-selectedRowCount": { visibility: "hidden" },
              ...datagridSx,
            }}
            paginationMode={isServerPagination ? "server" : "client"}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationChange}
            rowCount={isServerPagination ? effectiveTotal : undefined}
            pageSizeOptions={pageSizeOptions}
            filterMode={filterMode}
            filterModel={filterModel}
            onFilterModelChange={onFilterChange}
            getRowHeight={getRowHeight}
            localeText={{
              toolbarColumns: "",
              toolbarFilters: "",
              toolbarDensity: "",
              toolbarExport: "",
              toolbarExportCSV: "Descargar CSV",
              toolbarExportPrint: "Imprimir",
              footerRowSelected: () => "",
            }}
            initialState={{
              pagination: { paginationModel: { pageSize: 10, page: 0 } },
            }}
          />
        </Box>
      )}
    </Paper>
  );
}
